import React, { useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const FALLBACK_IMAGE =
    "https://res.cloudinary.com/computer-geek/image/upload/v1757460903/Salesive/water_mark_jzbrjq.png";

/**
 * Format price with currency symbol.
 */
function formatPrice(price, currency) {
    if (price == null) return "";
    const symbol = currency?.symbol || "₦";
    return `${symbol}${price.toLocaleString()}`;
}

/**
 * Dropdown showing product suggestions for @mention in chat input.
 */
export function ProductMentionDropdown({
    isOpen,
    results,
    isLoading,
    query,
    activeIndex,
    onSelect,
    darkMode = false,
}) {
    const listRef = useRef(null);

    // Scroll active item into view
    useEffect(() => {
        if (!listRef.current) return;
        const activeEl = listRef.current.children[activeIndex];
        if (activeEl) {
            activeEl.scrollIntoView({ block: "nearest" });
        }
    }, [activeIndex]);

    if (!isOpen) return null;

    const hasQuery = query.trim().length > 0;

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15 }}
                    className={`tw-absolute tw-bottom-full tw-left-0 tw-right-0 tw-mb-1 tw-mx-2 tw-rounded-xl tw-shadow-lg tw-border tw-overflow-hidden tw-z-50 ${
                        darkMode
                            ? "tw-bg-gray-800 tw-border-gray-700"
                            : "tw-bg-white tw-border-gray-200"
                    }`}
                    style={{ maxHeight: "280px" }}
                    onMouseDown={(e) => e.preventDefault()}
                >
                    {/* Header */}
                    <div
                        className={`tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-border-b ${
                            darkMode
                                ? "tw-text-gray-400 tw-border-gray-700"
                                : "tw-text-gray-500 tw-border-gray-100"
                        }`}
                    >
                        {hasQuery ? `Products matching "${query}"` : "Type to search products"}
                    </div>

                    {/* Loading state */}
                    {isLoading && (
                        <div className="tw-flex tw-items-center tw-justify-center tw-py-6">
                            <div className="tw-w-5 tw-h-5 tw-border-2 tw-border-primary/30 tw-border-t-primary tw-rounded-full tw-animate-spin" />
                        </div>
                    )}

                    {/* No results */}
                    {!isLoading && hasQuery && results.length === 0 && (
                        <div
                            className={`tw-px-3 tw-py-6 tw-text-center tw-text-sm ${
                                darkMode ? "tw-text-gray-500" : "tw-text-gray-400"
                            }`}
                        >
                            No products found
                        </div>
                    )}

                    {/* Empty state - no query yet */}
                    {!isLoading && !hasQuery && (
                        <div
                            className={`tw-px-3 tw-py-6 tw-text-center tw-text-sm ${
                                darkMode ? "tw-text-gray-500" : "tw-text-gray-400"
                            }`}
                        >
                            Start typing a product name...
                        </div>
                    )}

                    {/* Results list */}
                    {!isLoading && results.length > 0 && (
                        <div
                            ref={listRef}
                            className="tw-overflow-y-auto"
                            style={{ maxHeight: "240px" }}
                        >
                            {results.map((product, index) => {
                                const isActive = index === activeIndex;
                                const currency = product.shop?.currency;
                                const hasPromo =
                                    product.promoPrice != null &&
                                    product.promoPrice > product.price;

                                return (
                                    <div
                                        key={product._id || product.id}
                                        className={`tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2.5 tw-cursor-pointer tw-transition-colors ${
                                            isActive
                                                ? darkMode
                                                    ? "tw-bg-gray-700"
                                                    : "tw-bg-primary/5"
                                                : darkMode
                                                  ? "hover:tw-bg-gray-750"
                                                  : "hover:tw-bg-gray-50"
                                        }`}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            onSelect(product);
                                        }}
                                    >
                                        {/* Product image */}
                                        <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-overflow-hidden tw-flex-shrink-0 tw-bg-gray-100">
                                            <img
                                                src={product.image || FALLBACK_IMAGE}
                                                alt={product.name}
                                                className="tw-w-full tw-h-full tw-object-cover"
                                                loading="lazy"
                                                onError={(e) => {
                                                    if (e.target.src !== FALLBACK_IMAGE) {
                                                        e.target.src = FALLBACK_IMAGE;
                                                    }
                                                }}
                                            />
                                        </div>

                                        {/* Product info */}
                                        <div className="tw-flex-1 tw-min-w-0">
                                            <p
                                                className={`tw-text-sm tw-font-medium tw-truncate tw-m-0 ${
                                                    darkMode
                                                        ? "tw-text-gray-200"
                                                        : "tw-text-gray-900"
                                                }`}
                                            >
                                                {product.name}
                                            </p>
                                            <div className="tw-flex tw-items-center tw-gap-1.5">
                                                <span
                                                    className={`tw-text-xs tw-font-semibold ${
                                                        darkMode
                                                            ? "tw-text-gray-300"
                                                            : "tw-text-gray-700"
                                                    }`}
                                                >
                                                    {formatPrice(
                                                        product.price,
                                                        currency
                                                    )}
                                                </span>
                                                {hasPromo && (
                                                    <span
                                                        className={`tw-text-xs tw-line-through ${
                                                            darkMode
                                                                ? "tw-text-gray-500"
                                                                : "tw-text-gray-400"
                                                        }`}
                                                    >
                                                        {formatPrice(
                                                            product.promoPrice,
                                                            currency
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    );
}
